import React, { useState, useEffect, useCallback } from "react";
import styles from "./UserManagement.module.css";
import { CustomPagination, CustomTable } from "../../components";
import { AxiosInstance } from "../../services/Api";
import PopupModal from "../../components/PopupModal/PopupModal";
import { useNavigate } from "react-router-dom";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();
  // Menu State
  const [menuOpen, setMenuOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",   // "success" | "error"
    message: "",
    redirectPath: "",
  });
  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await AxiosInstance.get(
        `/licenses/user?page=${page}&limit=${limit}`
      );

      if (response?.data?.success) {
        setUsers(response?.data?.data?.licenses || []);

        if (response?.data?.data?.pagination) {
          setPagination({
            current_page: response.data.data.pagination.current_page || 1,
            per_page: response.data.data.pagination.per_page || 10,
            total: response.data.data.pagination.total || 0,
            total_pages: response.data.data.pagination.total_pages || 0,
            has_next: response.data.data.pagination.has_next || false,
            has_prev: response.data.data.pagination.has_prev || false,
          });
        }
      } else {
        throw new Error(response?.data?.message || "Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(
        err?.response?.data?.message || err?.message || "An error occurred"
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchUsers();
  }, [page, limit, fetchUsers]);

  // Handle delete user
  const handleDeleteUser = useCallback(
    async (row) => {
      if (!row?.id) {
        alert("Invalid user ID");
        return;
      }

      const confirmAction = window.confirm(
        `Are you sure you want to delete ${row?.email || row?.name}?`
      );

      if (!confirmAction) return;

      setDeletingId(row.id);

      try {
        const response = await AxiosInstance.delete(`/licenses/${row.id}`);

        if (response?.status === 200 && response?.data?.success) {
          setModalState({
            open: true,
            type: "success",
            message: response?.data?.message || "User deleted successfully!",
            redirectPath: "",
          });
          fetchUsers();
        } else {
          setModalState({
            open: true,
            type: "error",
            message: response?.data?.message || "Failed to delete user",
            redirectPath: "",
          });

          throw new Error(
            response?.data?.message || "Failed to delete user"
          );
        }
      } catch (err) {
        console.error("Error deleting user:", err);
        setModalState({
          open: true,
          type: "error",
          message: err?.response?.data?.message ||
            err?.message ||
            "Failed to delete user",
          redirectPath: "",
        });
      } finally {
        setDeletingId(null);
      }
    },
    [fetchUsers]
  );

  // Handle validate user (resend activation)
  const handleValidateUser = useCallback(
    async (row) => {
      if (!row?.id) {
        alert("Invalid user ID");
        return;
      }

      const confirmAction = window.confirm(
        `Are you sure you want to resend activation email to ${
          row?.email || row?.name
        }?`
      );

      if (!confirmAction) return;

      try {
        const response = await AxiosInstance.post(
          `/licenses/${row.id}/resend-activation`
        );

        if (response?.status === 200 && response?.data?.success) {

          setModalState({
            open: true,
            type: "success",
            message: response?.data?.message || "User validated successfully!",
            redirectPath: "",
          });
          fetchUsers();
        } else {
          throw new Error(
            response?.data?.message || "Failed to validate user"
          );
        }
      } catch (err) {
        console.error("Error validating user:", err);

        setModalState({
          open: true,
          type: "error",
          message: err?.response?.data?.message ||
            err?.message ||
            "Failed to validate user",
          redirectPath: "",
        });
      }
    },
    [fetchUsers]
  );

  // Table columns configuration
  const columns = [
    {
      key: "role_display_name",
      label: "ROLE NAME",
      minWidth: "180px",
      render: (value) => value || "-",
    },
    {
      key: "name",
      label: "USER NAME",
      minWidth: "180px",
      render: (value) => value || "-",
    },
    {
      key: "email",
      label: "USER EMAIL",
      minWidth: "250px",
      render: (value) => value || "-",
    },
    {
      key: "school_name",
      label: "SCHOOL NAME",
      minWidth: "200px",
      render: (value) => value || "-",
    },
    {
      key: "is_validated",
      label: "IS VALIDATED",
      width: "120px",
      align: "left",
      render: (value) => (value === 1 ? "Yes" : "No"),
    },
  ];

  // Action buttons configuration
  const actionButtons = [
    {
      id: "validate",
      label: "Validate",
      icon: <i className="fa-solid fa-location-crosshairs" />,
      variant: "validate",
      tooltip: "Validate",
      onClick: (row) => handleValidateUser(row),
      style: (row) => ({
        backgroundColor:
          row.is_validated === 0
            ? "var(--color-error)"
            : "var(--color-success)",
      }),
    },
    {
      id: "deletes",
      label: "Delete",
      icon: <i className="fa-solid fa-trash" />,
      variant: "deletes",
      tooltip: "Delete",
      onClick: (row) => handleDeleteUser(row),
      loading: (row) => deletingId === row.id,
    },
  ];

  // Toggle menu
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const handleListAll = () => {
    setMenuOpen(false);
    alert("List All Users");
  };

  const handleDeletedUsers = () => {
    setMenuOpen(false);
    alert("Show Deleted Users");
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (

    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>USER MANAGEMENT</h1>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.menuButton}
              onClick={toggleMenu}
              aria-label="More options"
            >
              <i
                className="fa-solid fa-ellipsis-vertical"
                style={{ fontSize: "18px", color: "#333" }}
              />
            </button>

            {menuOpen && (
              <div className={styles.dropdownMenu}>
                <button className={styles.menuItem} onClick={handleListAll}>
                  List All
                </button>
                <button className={styles.menuItem} onClick={handleDeletedUsers}>
                  Deleted User
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className={styles.errorMessage}>
            {error}
            <button onClick={fetchUsers} className={styles.retryButton}>
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        <div className={styles.tableSection}>
          <CustomTable
            columns={columns}
            rows={users}
            actionButtons={actionButtons}
            loading={loading}
            emptyMessage="No users found"
            hoverable
            rowKeyField="id"
          />
        </div>

        {/* Pagination */}
        <div className={styles.paginationSection}>
          <CustomPagination
            page={pagination.current_page}
            limit={pagination.per_page}
            total={pagination.total}
            onPageChange={handlePageChange}
            showLimitSelector={false}
            siblingCount={3}
            showFirstLast
            showPrevNext
          />
        </div>
      </div>
    </>


  );
};

export default UserManagement;